'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const engagements = [
  {
    type: 'Assessment Engagement',
    duration: '2–3 weeks',
    desc: 'Planning maturity assessment, architecture review, and gap analysis. Deliverable: structured findings report with prioritised recommendations.',
  },
  {
    type: 'Planning Architecture Design',
    duration: '4–6 weeks',
    desc: 'Full design of the planning data architecture, model structure, and integration approach before build begins.',
  },
  {
    type: 'Implementation Support',
    duration: '12–20 weeks',
    desc: 'Hands-on support through the full SAP Activate implementation lifecycle, from Explore through Deploy.',
  },
  {
    type: 'Planning Optimisation',
    duration: '4–8 weeks',
    desc: 'Review of an existing SAC Planning implementation. Identify and resolve performance, logic, and governance issues.',
  },
  {
    type: 'Advisory Retainer',
    duration: 'Monthly',
    desc: 'Ongoing advisory support for planning teams running SAC Planning. Architecture guidance, model reviews, and operational support.',
  },
];

export default function EngagementModel() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6">
      <div className="max-w-container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 reveal-on-scroll">
            <div className="mono-label mb-3">How We Work</div>
            <h2
              className="font-heading font-semibold text-foreground mb-4"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', letterSpacing: '-0.025em' }}
            >
              Engagement Model
            </h2>
            <p className="text-sm text-muted leading-relaxed font-body mb-8">
              Engagements are structured to deliver clear outputs at each stage.
              No open-ended retainers without defined scope.
            </p>
            <Link href="/consulting-services" className="btn-primary text-sm">
              View All Services →
            </Link>
          </div>

          <div className="md:col-span-8 reveal-on-scroll reveal-delay-2">
            <div className="flex flex-col">
              {engagements.map((eng, i) => (
                <div
                  key={eng.type}
                  className="py-6 border-b border-border last:border-0 flex flex-col md:flex-row gap-4"
                >
                  <div className="md:w-48 shrink-0">
                    <div className="font-heading font-semibold text-foreground text-sm">
                      {eng.type}
                    </div>
                    <div className="mono-label mt-1">{eng.duration}</div>
                  </div>
                  <p className="text-sm text-muted leading-relaxed font-body">{eng.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}